import React, { useState } from 'react';
import KoruLogo from '../KoruLogo';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';
import { CancelDeletionPage } from './CancelDeletionPage';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { ResetPasswordForm } from './ResetPasswordForm';
import { ThemeToggle } from '../ui/ThemeToggle';

type AuthMode = 'login' | 'register' | 'cancel-deletion' | 'forgot-password' | 'reset-password';

interface AuthPageProps {
  onSuccess?: () => void;
  initialMode?: AuthMode;
}

export const AuthPage: React.FC<AuthPageProps> = ({
  onSuccess,
  initialMode = 'login',
}) => {
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [resetEmail, setResetEmail] = useState<string>('');

  const handleSuccess = () => {
    onSuccess?.();
  };

  const handleForgotPasswordSuccess = (email: string) => {
    setResetEmail(email);
    setMode('reset-password');
  };

  const handleResetPasswordSuccess = () => {
    setMode('login');
  };

  // If in cancel-deletion mode, show the cancellation page
  if (mode === 'cancel-deletion') {
    return (
      <CancelDeletionPage 
        onBack={() => setMode('login')}
      />
    );
  }

  // If in forgot-password mode, show the forgot password form
  if (mode === 'forgot-password') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        {/* Theme Toggle - Fixed Position */}
        <div className="fixed top-4 right-4 z-10">
          <ThemeToggle />
        </div>
        
        {/* Header */}
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <div className="w-12 h-12 rounded-lg overflow-hidden">
              <KoruLogo size={48} />
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900 dark:text-gray-100">
            Koru
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <ForgotPasswordForm
            onSuccess={handleForgotPasswordSuccess}
            onBack={() => setMode('login')}
          />
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            © 2024 Koru. All rights reserved.
          </p>
        </div>
      </div>
    );
  }

  // If in reset-password mode, show the reset password form
  if (mode === 'reset-password') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        {/* Theme Toggle - Fixed Position */}
        <div className="fixed top-4 right-4 z-10">
          <ThemeToggle />
        </div>
        
        {/* Header */}
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <div className="w-12 h-12 rounded-lg overflow-hidden">
              <KoruLogo size={48} />
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900 dark:text-gray-100">
            Koru
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <ResetPasswordForm
            email={resetEmail}
            onSuccess={handleResetPasswordSuccess}
            onBack={() => setMode('login')}
          />
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            © 2024 Koru. All rights reserved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Theme Toggle - Fixed Position */}
      <div className="fixed top-4 right-4 z-10">
        <ThemeToggle />
      </div>
      
      {/* Header */}
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="w-12 h-12 bg-[#0B1629] rounded-lg flex items-center justify-center">
            <KoruLogo size={32} />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900 dark:text-gray-100">
          Koru
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          Build better habits, one day at a time
        </p>
      </div>

      {/* Auth Forms */}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        {mode === 'login' ? (
          <LoginForm
            onSuccess={handleSuccess}
            onSwitchToRegister={() => setMode('register')}
            onSwitchToCancelDeletion={() => setMode('cancel-deletion')}
            onSwitchToForgotPassword={() => setMode('forgot-password')}
          />
        ) : (
          <RegisterForm
            onSuccess={handleSuccess}
            onSwitchToLogin={() => setMode('login')}
          />
        )}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          © 2024 Koru. All rights reserved.
        </p>
      </div>
    </div>
  );
}; 